import Link from 'next/link';
import { useState } from 'react';

import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from '@radix-ui/react-popover';

import DropdownIcon from '@publicImages/svg/chevron-down.svg?static';
import QuickFindLabel from '@publicImages/svg/quick-find-label.svg?static';
import QuickFindIcon from '@publicImages/svg/quick-find.svg?static';

import UIResource from '@/components/UIResource';
import { ACCESS_KEY_PARAM } from '@/ducks/accessKeys/constants';
import { selectDisplayedQuickFindLinks } from '@/ducks/common/lookup/selectors';
import { type TLookupQuickFindLink } from '@/ducks/common/lookup/types';
import { FILTER_KEY } from '@/ducks/filters/types';
import { selectIsMnvv } from '@/ducks/mnvv/selectors';
import getCurrentSearchParams from '@/helpers/url/getCurrentSearchParams';
import { useAppSelector } from '@/store';

import QuickFindWarningDialog from '../QuickFindWarningDialog';

import styles from './QuickFindFilters.module.scss';

export const makeQFUrl = ({ linkUrl, quickFindId }: TLookupQuickFindLink) =>
  `?${FILTER_KEY.quickFindID}=${quickFindId}&${linkUrl}`;

const QuickFindFilters = () => {
  const quickFindLinks = useAppSelector(selectDisplayedQuickFindLinks);
  const [open, setOpen] = useState(false);

  const searchParams = getCurrentSearchParams();
  const isAccessKey = !!searchParams.get(ACCESS_KEY_PARAM);
  const isMNVV = useAppSelector(selectIsMnvv);

  const handleClose = () => setOpen(false);

  if (!quickFindLinks.length || isAccessKey || isMNVV) {
    return null;
  }

  return (
    <div className={styles.root}>
      <ul className={styles.list}>
        <span className={styles.title}>
          <QuickFindIcon className={styles.titleIcon} />
          <UIResource id="QuickFind.label" />
        </span>

        {quickFindLinks.map((item) => (
          <li className={styles.listItem} key={item.quickFindId}>
            <Link className={styles.link} href={makeQFUrl(item)} onClick={handleClose} prefetch={false} scroll={false}>
              <QuickFindLabel className={styles.linkIcon} />
              <span>{item.linkTitle}</span>
            </Link>
          </li>
        ))}
      </ul>

      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <button className={styles.dropdownButton}>
            <QuickFindIcon className={styles.dropdownSearchIcon} />
            <UIResource id="QuickFind.label" />
            <DropdownIcon className={styles.dropdownButtonIcon} />
          </button>
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent className={styles.popoverContent}>
            <ul className={styles.list}>
              {quickFindLinks.map((item) => (
                <li className={styles.listItem} key={item.quickFindId}>
                  <Link
                    className={styles.link}
                    href={makeQFUrl(item)}
                    onClick={handleClose}
                    prefetch={false}
                    scroll={false}
                  >
                    <QuickFindLabel className={styles.linkIcon} />
                    <span>{item.linkTitle}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </PopoverPortal>
      </Popover>

      <QuickFindWarningDialog />
    </div>
  );
};

export default QuickFindFilters;
